import React, { useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import PhoneIcon from "@mui/icons-material/SettingsPhone";
import WebhookIcon from "@mui/icons-material/Webhook";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";

import VoIPDashboard from "./VoIPDashboard";
import CallHistory from "./CallHistory";
import VoIPSettings from "./VoIPSettings";
import SIPConfig from "./SIPConfig";
import WebhookConfig from "./WebhookConfig";

const menuSections = [
  {
    title: "Visao Geral",
    items: [
      { id: "dashboard", label: "Dashboard", icon: DashboardIcon },
      { id: "history", label: "Historico de Chamadas", icon: HistoryIcon },
    ],
  },
  {
    title: "Configuracoes",
    items: [
      { id: "settings", label: "Configuracoes Gerais", icon: SettingsIcon },
    ],
  },
  {
    title: "Integracoes",
    items: [
      { id: "sip", label: "SIP", icon: PhoneIcon },
      { id: "webhook", label: "Webhook", icon: WebhookIcon },
    ],
  },
];

export default function VoIPPanel({ connectionId, onBack }) {
  const [activeSection, setActiveSection] = useState("dashboard");

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return <VoIPDashboard />;
      case "history":
        return <CallHistory />;
      case "settings":
        return <VoIPSettings connectionId={connectionId} />;
      case "sip":
        return <SIPConfig connectionId={connectionId} />;
      case "webhook":
        return <WebhookConfig connectionId={connectionId} />;
      default:
        return <VoIPDashboard />;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "calc(100vh - 64px)",
        bgcolor: "#0B0F19",
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: 260,
          minWidth: 260,
          bgcolor: "#111827",
          borderRight: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Sidebar header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {onBack && (
            <Tooltip title="Voltar">
              <IconButton
                size="small"
                onClick={onBack}
                sx={{ color: "#6B7280", "&:hover": { color: "#F3F4F6" } }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <PhoneInTalkIcon sx={{ color: "#8B5CF6", fontSize: 24 }} />
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "#F3F4F6", fontWeight: 700, lineHeight: 1.2 }}
            >
              OtimizAPP VoIP
            </Typography>
            <Typography sx={{ color: "#6B7280", fontSize: "0.7rem" }}>
              Painel de chamadas
            </Typography>
          </Box>
        </Box>

        {/* Menu sections */}
        <Box sx={{ flex: 1, py: 1 }}>
          {menuSections.map((section, idx) => (
            <Box key={section.title}>
              {idx > 0 && (
                <Divider
                  sx={{
                    borderColor: "rgba(255,255,255,0.04)",
                    my: 1,
                  }}
                />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: "#4B5563",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  px: 2.5,
                  py: 1,
                  display: "block",
                }}
              >
                {section.title}
              </Typography>
              <List dense disablePadding>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  return (
                    <ListItemButton
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      sx={{
                        px: 2.5,
                        py: 0.8,
                        mx: 1,
                        borderRadius: "8px",
                        bgcolor: isActive
                          ? "rgba(139, 92, 246, 0.1)"
                          : "transparent",
                        "&:hover": {
                          bgcolor: isActive
                            ? "rgba(139, 92, 246, 0.15)"
                            : "rgba(255,255,255,0.03)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Icon
                          sx={{
                            fontSize: 18,
                            color: isActive ? "#8B5CF6" : "#6B7280",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.82rem",
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? "#D8B4FE" : "#D1D5DB",
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, p: 4, overflow: "auto" }}>{renderContent()}</Box>
    </Box>
  );
}
